<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  pdfDocument: { type: Object, required: true },
  pageNumber: { type: Number, required: true },
  scale: { type: Number, required: true },
  basePageSize: { type: Object, required: true },
  scrollRoot: { default: null },
  isPriority: { type: Boolean, default: false }
})
const emit = defineEmits(['render-error'])

const pageElement = ref(null)
const canvas = ref(null)
const isVisible = ref(props.isPriority)
const isRendered = ref(false)
const actualPageSize = ref(null)

// Reserve every page's full height before its canvas is rendered. Without this,
// lazy-loading pages start at a short placeholder height and move subsequent pages.
const pageWidth = computed(() => actualPageSize.value?.width ?? props.basePageSize.width)
const pageHeight = computed(() => actualPageSize.value?.height ?? props.basePageSize.height)
const displayWidth = computed(() => Math.ceil(pageWidth.value * props.scale))
const displayHeight = computed(() => Math.ceil(pageHeight.value * props.scale))

let observer = null
let renderTask = null
let renderVersion = 0
let renderQueue = Promise.resolve()

function observePage() {
  observer?.disconnect()
  observer = null
  if (!pageElement.value || typeof IntersectionObserver === 'undefined') {
    isVisible.value = true
    return
  }
  observer = new IntersectionObserver((entries) => {
    if (!entries[0]?.isIntersecting) return
    isVisible.value = true
    observer?.disconnect()
    observer = null
  }, { root: props.scrollRoot, rootMargin: '900px 0px' })
  observer.observe(pageElement.value)
}

async function renderPage(version) {
  if (!isVisible.value || !canvas.value || version !== renderVersion) return

  try {
    const page = await props.pdfDocument.getPage(props.pageNumber)
    if (version !== renderVersion || !canvas.value) return

    // The first page provides stable placeholders; use this page's exact size
    // when mixed-size PDF pages are present.
    const unitViewport = page.getViewport({ scale: 1 })
    if (pageWidth.value !== unitViewport.width || pageHeight.value !== unitViewport.height) {
      actualPageSize.value = { width: unitViewport.width, height: unitViewport.height }
    }

    const viewport = page.getViewport({ scale: props.scale })
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
    const targetCanvas = canvas.value
    targetCanvas.width = Math.max(1, Math.ceil(viewport.width * pixelRatio))
    targetCanvas.height = Math.max(1, Math.ceil(viewport.height * pixelRatio))
    const context = targetCanvas.getContext('2d', { alpha: false })
    if (!context) throw new Error('Canvas 2D context is unavailable')

    const task = page.render({
      canvasContext: context,
      viewport,
      transform: pixelRatio === 1 ? null : [pixelRatio, 0, 0, pixelRatio, 0, 0]
    })
    renderTask = task
    try {
      await task.promise
      if (version === renderVersion) isRendered.value = true
    } finally {
      if (renderTask === task) renderTask = null
    }
  } catch (cause) {
    if (cause?.name !== 'RenderingCancelledException' && version === renderVersion) {
      emit('render-error', `페이지 ${props.pageNumber}을(를) 표시하지 못했습니다.`)
    }
  }
}

function queueRender() {
  const version = ++renderVersion
  isRendered.value = false
  renderTask?.cancel()
  // Serialize renders: PDF.js must not render twice into the same canvas until
  // the previous renderTask has rejected/resolved after cancellation.
  renderQueue = renderQueue.catch(() => {}).then(() => renderPage(version))
}

watch(() => props.scrollRoot, observePage)
watch(() => props.pdfDocument, () => { actualPageSize.value = null })
watch([isVisible, () => props.pdfDocument, () => props.pageNumber, () => props.scale], queueRender)
onMounted(() => {
  observePage()
  queueRender()
})
onBeforeUnmount(() => {
  renderVersion += 1
  observer?.disconnect()
  renderTask?.cancel()
})
</script>

<template>
  <article
    ref="pageElement"
    class="pdf-page-canvas"
    :style="{ width: `${displayWidth}px`, height: `${displayHeight}px` }"
    :class="{ 'is-rendered': isRendered }"
    :data-pdf-page="pageNumber"
    :aria-label="`리포트 ${pageNumber}페이지`"
  >
    <canvas ref="canvas" />
    <span v-if="!isRendered" class="pdf-page-canvas__loading">{{ pageNumber }}페이지 렌더링 중</span>
  </article>
</template>

<style scoped>
.pdf-page-canvas { position: relative; display: grid; place-items: center; flex: 0 0 auto; background: #fff; box-shadow: 0 12px 30px rgba(11, 27, 51, .18); }
.pdf-page-canvas canvas { display: block; width: 100%; height: 100%; max-width: none; visibility: hidden; }
.pdf-page-canvas.is-rendered canvas { visibility: visible; }
.pdf-page-canvas__loading { position: absolute; color: var(--text-muted); font-size: var(--text-sm); font-weight: 700; }
.pdf-page-canvas.is-rendered .pdf-page-canvas__loading { display: none; }
</style>
